import { View, StyleSheet, Pressable } from "react-native";
import { useNavigation, useRoute, NavigationProp, RouteProp } from "@react-navigation/native";

import { RootStackParamList } from "../../navigation/AppNavigator.web";
import CustomText from "../../components/common/general/CustomText/CustomText";

interface AdminBreadcrumbsProps {
  currentRoute: string;
}

interface BreadcrumbItem {
  label: string;
  route: keyof RootStackParamList;
  clickable: boolean;
  routeParams?: Record<string, string | undefined>;
}

function AdminBreadcrumbs({ currentRoute }: AdminBreadcrumbsProps) {
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const route = useRoute<RouteProp<RootStackParamList>>();
  const params = route.params as Record<string, string | undefined>;

  const getPageDisplayName = (routeName: string) => {
    const pageMap: { [key: string]: string } = {
      "Admin Dashboard": "Dashboard",
      "Manage Courses": "Manage Courses",
      Teaching: "Teaching",
      "Degrees & Programs": "Degrees & Programs",
      Students: "Students",
      "Student Profile": "Student Profile",
      "Degree Information": "Degree Information",
      "Course Information": "Course Information",
      "Admin Users": "Users",
      "Admin Announcements": "Announcements",
      Analytics: "Analytics",
      "Admin Settings": "Settings",
      "Custom Coursework Modules": "Course Editor",
    };
    return pageMap[routeName] || routeName;
  };

  const buildBreadcrumbs = (): BreadcrumbItem[] => {
    const breadcrumbs: BreadcrumbItem[] = [
      {
        label: "Admin",
        route: "Admin Dashboard",
        clickable: true,
      },
    ];

    switch (currentRoute) {
      case "Student Profile": {
        breadcrumbs.push({
          label: "Students",
          route: "Students",
          clickable: true,
        });
        if (params?.studentName) {
          breadcrumbs.push({
            label: params.studentName,
            route: currentRoute as keyof RootStackParamList,
            clickable: false,
          });
        } else {
          breadcrumbs.push({
            label: getPageDisplayName(currentRoute),
            route: currentRoute as keyof RootStackParamList,
            clickable: false,
          });
        }
        break;
      }

      case "Course Information": {
        const parentRoute = params?.returnTo || "Manage Courses";
        const parentLabel = getPageDisplayName(parentRoute);

        breadcrumbs.push({
          label: parentLabel,
          route: parentRoute as keyof RootStackParamList,
          clickable: true,
        });

        if (params?.courseName) {
          breadcrumbs.push({
            label: params.courseName,
            route: currentRoute as keyof RootStackParamList,
            clickable: false,
          });
        } else {
          breadcrumbs.push({
            label: getPageDisplayName(currentRoute),
            route: currentRoute as keyof RootStackParamList,
            clickable: false,
          });
        }
        break;
      }

      case "Custom Coursework Modules": {
        // Determine the return route hierarchy
        const returnRoute = params?.returnTo || "Manage Courses";
        const returnLabel = getPageDisplayName(returnRoute);

        breadcrumbs.push({
          label: returnLabel,
          route: returnRoute as keyof RootStackParamList,
          clickable: true,
        });

        if (params?.courseName) {
          if (returnRoute === "Teaching") {
            breadcrumbs.push({
              label: params.courseName,
              route: "Course Information",
              clickable: true,
              routeParams: {
                courseId: params?.courseId,
                courseName: params?.courseName,
                returnTo: returnRoute,
              },
            });
          } else {
            breadcrumbs.push({
              label: params.courseName,
              route: "Course Information",
              clickable: true,
              routeParams: {
                courseId: params?.courseId,
                courseName: params?.courseName,
                returnTo: returnRoute,
              },
            });
          }
        }

        breadcrumbs.push({
          label: getPageDisplayName(currentRoute),
          route: currentRoute as keyof RootStackParamList,
          clickable: false,
        });
        break;
      }

      case "Degree Information": {
        const parentRoute = params?.returnTo || "Degrees & Programs";
        const parentLabel = getPageDisplayName(parentRoute);

        breadcrumbs.push({
          label: parentLabel,
          route: parentRoute as keyof RootStackParamList,
          clickable: true,
        });

        if (params?.degreeName) {
          breadcrumbs.push({
            label: params.degreeName,
            route: currentRoute as keyof RootStackParamList,
            clickable: false,
          });
        } else {
          breadcrumbs.push({
            label: getPageDisplayName(currentRoute),
            route: currentRoute as keyof RootStackParamList,
            clickable: false,
          });
        }
        break;
      }

      default: {
        breadcrumbs.push({
          label: getPageDisplayName(currentRoute),
          route: currentRoute as keyof RootStackParamList,
          clickable: false,
        });
        break;
      }
    }

    return breadcrumbs;
  };

  const handleBreadcrumbClick = (item: BreadcrumbItem) => {
    if (!item.clickable) return;

    try {
      if (item.routeParams) {
        switch (item.route) {
          case "Course Information":
            navigation.navigate("Course Information", {
              courseId: item.routeParams.courseId || "",
              courseName: item.routeParams.courseName,
              returnTo: item.routeParams.returnTo,
            });
            break;
          case "Student Profile":
            navigation.navigate("Student Profile", {
              studentId: item.routeParams.studentId || "",
              studentName: item.routeParams.studentName,
              returnTo: item.routeParams.returnTo,
            });
            break;
          default:
            navigation.navigate("Admin Dashboard");
            break;
        }
      } else {
        switch (item.route) {
          case "Admin Dashboard":
            navigation.navigate("Admin Dashboard");
            break;
          case "Manage Courses":
            navigation.navigate("Manage Courses");
            break;
          case "Teaching":
            navigation.navigate("Teaching");
            break;
          case "Students":
            navigation.navigate("Students");
            break;
          case "Degrees & Programs":
            navigation.navigate("Degrees & Programs");
            break;
          case "Admin Users":
            navigation.navigate("Admin Users");
            break;
          case "Admin Announcements":
            navigation.navigate("Admin Announcements");
            break;
          case "Admin Settings":
            navigation.navigate("Admin Settings");
            break;
          default:
            navigation.navigate("Admin Dashboard");
            break;
        }
      }
    } catch (error) {
      console.error("Navigation error:", error);
      navigation.navigate("Admin Dashboard");
    }
  };

  const breadcrumbs = buildBreadcrumbs();

  return (
    <View style={styles.breadcrumbs}>
      {breadcrumbs.map((item, index) => (
        <View key={index} style={styles.breadcrumbItem}>
          {item.clickable ? (
            <Pressable onPress={() => handleBreadcrumbClick(item)}>
              <CustomText
                text={item.label}
                size="m"
                weight="medium"
                useTranslationText={false}
                style={styles.clickableText}
              />
            </Pressable>
          ) : (
            <CustomText
              text={item.label}
              size="m"
              weight="medium"
              useTranslationText={false}
              style={styles.currentPage}
            />
          )}

          {index < breadcrumbs.length - 1 && (
            <CustomText text=">" size="m" weight="medium" useTranslationText={false} style={styles.separator} />
          )}
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  breadcrumbs: {
    flexDirection: "row",
    alignItems: "center",
  },
  breadcrumbItem: {
    flexDirection: "row",
    alignItems: "center",
  },
  clickableText: {
    color: "#3E68FE",
    cursor: "pointer",
  },
  separator: {
    color: "#9CA3AF",
    marginHorizontal: 8,
  },
  currentPage: {
    color: "#374151",
  },
});

export default AdminBreadcrumbs;
